/**
 * Bouton flottant pour installer la PWA sur Android.
 * Sur iOS, affiche une instruction.
 */
import React, { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const STORAGE_KEY = 'portail_install_dismissed';

export const InstallPrompt: React.FC = () => {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIos, setIsIos] = useState(false);
  const [show, setShow] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    // Détection iOS Safari
    const ua = window.navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
    const standalone = (window.navigator as any).standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches;
    setIsIos(ios && !standalone);

    if (localStorage.getItem(STORAGE_KEY)) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Sur iOS, afficher la hint si pas en standalone
    if (ios && !standalone) {
      setTimeout(() => setShow(true), 2000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const install = async () => {
    if (deferred) {
      try {
        await deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice.outcome === 'accepted') {
          setShow(false);
        }
      } catch {
        // ignore
      }
      setDeferred(null);
    } else if (isIos) {
      setShowIosHint(true);
    }
  };

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(STORAGE_KEY, '1');
  };

  if (!show) return null;

  return (
    <>
      <div className="fixed bottom-4 left-4 right-4 max-w-md mx-auto z-40 bg-blue-600 text-white rounded-2xl shadow-2xl p-4 flex items-center gap-3 animate-slide-up">
        <div className="text-2xl flex-shrink-0">📱</div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">Installer l'application</div>
          <div className="text-xs text-blue-100 mt-0.5">
            Ajoutez le portail 2i à votre écran d'accueil
          </div>
        </div>
        <button
          onClick={install}
          className="px-3 py-1.5 bg-gray-900 text-blue-300 text-xs font-bold rounded-lg hover:bg-blue-900/30"
        >
          Installer
        </button>
        <button
          onClick={dismiss}
          className="text-white/70 hover:text-white text-xl leading-none"
          aria-label="Fermer"
        >
          ×
        </button>
      </div>

      {showIosHint && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowIosHint(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-gray-900 dark:text-white mb-2">📱 Installer sur iPhone / iPad</h3>
            <ol className="space-y-2 text-sm text-gray-700 dark:text-gray-300 list-decimal list-inside mt-3">
              <li>Appuyez sur l'icône <strong>Partager</strong> en bas de Safari (carré + flèche ↑)</li>
              <li>Faites défiler et choisissez <strong>« Sur l'écran d'accueil »</strong></li>
              <li>Appuyez sur <strong>« Ajouter »</strong></li>
            </ol>
            <button
              onClick={() => setShowIosHint(false)}
              className="mt-4 w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
            >
              J'ai compris
            </button>
          </div>
        </div>
      )}
    </>
  );
};
